import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  lazy,
  Suspense,
  startTransition,
} from "react";
import AgoraStreamingClient from "../components/AgoraStreamingClient";
import CamWidget from "../components/camera/CamWidget";
import DroneMap from "../components/DroneMap";
import {
  UAVRCTelemetry,
  CoordinatesTelemetry,
} from "../components/TelemetryWidgets";
import JoystickControl from "../components/JoystickControl";
import LogoLoader from "../components/LogoLoader";
import PWALayout from "../components/PWALayout";
import PWAInstallPrompt from "../components/PWAInstallPrompt";
import "./StreamingPage.css";
// Import custom hooks
import useStreamApi from "../hooks/useStreamApi";
import useMqttTelemetry from "../hooks/useMqttTelemetry"; // Temporarily add back for logging
import useResponsive from "../hooks/useResponsive";
import { useParams } from "react-router-dom";
import ReactModal from "react-modal";
import MobileStatusWidget from "../components/MobileStatusWidget";
import MobileViewToggle from "../components/MobileViewToggle";
import MultiviewLayout from "../components/MultiviewLayout";
import CameraPiPView from "../components/CameraPiPView";
import SplitScreenLayout from "../components/SplitScreenLayout";

// Lazy load MobileMap for better performance on mobile
const MobileMap = lazy(() => import("../components/MobileMap"));

const StreamingPage = () => {
  const [appId] = useState("2a6fdf6cf816485d8781fdbb58cb669d"); // Pre-filled App ID
  const [role] = useState("audience"); // Default role
  const [isJoined, setIsJoined] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Use responsive hook for device detection
  const {
    isMobile,
    isTablet,
    isDesktop,
    isMobileOrTablet,
    viewport,
    isLandscape,
    isPortrait,
  } = useResponsive();

  // Detect PWA mode (standalone)
  const [isPWAMode, setIsPWAMode] = useState(false);

  useEffect(() => {
    // Check if app is running in standalone mode (PWA)
    // Only run once on mount to prevent infinite loops
    if (window._pwaModeChecked) return;

    const checkPWAMode = () => {
      // Check URL parameter for testing: ?pwa=true
      const urlParams = new URLSearchParams(window.location.search);
      const forcePWA = urlParams.get("pwa") === "true";

      const isStandalone =
        forcePWA || // Force PWA mode for testing
        window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true ||
        document.referrer.includes("android-app://");

      setIsPWAMode(isStandalone);
      window._pwaModeChecked = true;

      // Log for debugging (only once)
      console.log("PWA Mode Check:", {
        forcePWA,
        isStandalone,
        displayMode: window.matchMedia("(display-mode: standalone)").matches,
        navigatorStandalone: window.navigator.standalone,
        referrer: document.referrer,
      });

      if (forcePWA) {
        console.log("✅ PWA Layout Mode: FORCED for testing");
      } else if (isStandalone) {
        console.log("✅ PWA Layout Mode: ACTIVE (standalone mode detected)");
      } else {
        console.log("❌ PWA Layout Mode: INACTIVE (browser mode)");
      }
    };

    checkPWAMode();

    // Listen for display mode changes (but only if not already checked)
    const mediaQuery = window.matchMedia("(display-mode: standalone)");
    const handleChange = () => {
      if (!window._pwaModeChecked) {
        checkPWAMode();
      }
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
    } else {
      mediaQuery.addListener(handleChange); // Fallback for older browsers
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []); // Empty dependency array - only run once on mount

  // Initialize video settings based on device type
  const getInitialVideoSettings = useCallback(() => {
    if (isMobile) {
      return {
        audioConfig: { AEC: true, AGC: true },
        videoConfig: {
          encoderConfig: {
            width: 480,
            height: 270,
            frameRate: 15,
            bitrateMin: 200,
            bitrateMax: 500,
          },
        },
      };
    } else if (isTablet) {
      return {
        audioConfig: { AEC: true, AGC: true },
        videoConfig: {
          encoderConfig: {
            width: 640,
            height: 360,
            frameRate: 15,
            bitrateMin: 400,
            bitrateMax: 800,
          },
        },
      };
    } else {
      // Desktop
      return {
        audioConfig: { AEC: true, AGC: true },
        videoConfig: {
          encoderConfig: {
            width: 1280,
            height: 720,
            frameRate: 30,
            bitrateMin: 600,
            bitrateMax: 1500,
          },
        },
      };
    }
  }, [isMobile, isTablet, isDesktop]);

  const [videoSettings, setVideoSettings] = useState(() =>
    getInitialVideoSettings()
  );

  // Update video settings when device type changes
  useEffect(() => {
    setVideoSettings(getInitialVideoSettings());
  }, [isMobile, isTablet, isDesktop]); // Depend on device type, not the callback function

  const { shareCode: urlShareCode } = useParams();
  const [shareCode, setShareCode] = useState(urlShareCode || null);
  const [flightPosition, setFlightPosition] = useState(null);
  const [joystickData, setJoystickData] = useState({
    x: 0,
    y: 0,
    angle: 0,
    distance: 0,
  });
  // Add state for widget visibility
  const [showAllWidgets, setShowAllWidgets] = useState(true);
  const [pinInput, setPinInput] = useState("");
  const [pinError, setPinError] = useState("");
  const [isPinVerified, setIsPinVerified] = useState(false);
  const [isVerifyingPin, setIsVerifyingPin] = useState(false);
  const [showMobileMap, setShowMobileMap] = useState(false);
  const [mobileView, setMobileView] = useState("camera"); // Always start with camera view
  const [isTelemetryExpanded, setIsTelemetryExpanded] = useState(false);

  // Use the custom hook for API calls
  const {
    streamSettings,
    isLoadingApi,
    apiError: streamApiError,
    retryFetchStreamData,
  } = useStreamApi(shareCode);

  // Memoize deviceId to prevent MQTT hook from reconnecting unnecessarily
  const deviceIdForMqtt = useMemo(() => {
    return streamSettings?.channel_name || null;
  }, [streamSettings?.channel_name]);

  // Handle stream revocation from MQTT
  const handleStreamRevoked = useCallback((payload) => {
    console.log("[StreamingPage] Stream has been REVOKED via MQTT:", payload);
    setIsJoined(false);
    setCombinedError(
      "This stream link has been revoked by the administrator. Access denied."
    );
  }, []);

  const {
    telemetryData,
    isConnected: isMqttConnected,
    mqttError,
    isRevoked,
  } = useMqttTelemetry(deviceIdForMqtt, shareCode, handleStreamRevoked);

  // Memoize telemetry values to prevent unnecessary re-renders
  const telemetryValues = useMemo(() => {
    if (!telemetryData || Object.keys(telemetryData).length === 0) {
      return null;
    }
    return {
      batteryLevel: telemetryData.powerBattery?.droneBatteryPower ?? 0,
      rcBatteryLevel: telemetryData.powerBattery?.rcBatteryPower ?? 0,
      gpsCount: telemetryData.locationNavigation?.gps?.count ?? 0,
      altitude: telemetryData.aircraftState?.height ?? 0,
      heading: telemetryData.aircraftState?.heading ?? 0,
      verticalSpeed: telemetryData.aircraftState?.speedY ?? 0,
      horizontalSpeed: telemetryData.aircraftState?.speedX ?? 0,
      flightMode: telemetryData.aircraftState?.flightMode ?? "MANUAL",
      distanceFromHome: telemetryData.aircraftState?.distanceToHome ?? 0,
      isFlying: telemetryData.aircraftState?.isFlying ?? false,
      lat: telemetryData.locationNavigation?.latitude,
      lng: telemetryData.locationNavigation?.longitude,
    };
  }, [
    telemetryData?.powerBattery?.droneBatteryPower,
    telemetryData?.powerBattery?.rcBatteryPower,
    telemetryData?.locationNavigation?.gps?.count,
    telemetryData?.aircraftState?.height,
    telemetryData?.aircraftState?.heading,
    telemetryData?.aircraftState?.speedY,
    telemetryData?.aircraftState?.speedX,
    telemetryData?.aircraftState?.flightMode,
    telemetryData?.aircraftState?.distanceToHome,
    telemetryData?.aircraftState?.isFlying,
    telemetryData?.locationNavigation?.latitude,
    telemetryData?.locationNavigation?.longitude,
  ]);

  // Log MQTT connection status (only log changes, not every render)
  const prevMqttStatus = useRef({ isConnected: false, error: null });
  useEffect(() => {
    if (prevMqttStatus.current.isConnected !== isMqttConnected) {
      console.log(
        "MQTT Connection Status:",
        isMqttConnected ? "Connected" : "Disconnected"
      );
      prevMqttStatus.current.isConnected = isMqttConnected;
    }
    if (prevMqttStatus.current.error !== mqttError && mqttError) {
      console.error("MQTT Error:", mqttError);
      prevMqttStatus.current.error = mqttError;
    }
  }, [isMqttConnected, mqttError]);

  // Log telemetry data when it changes
  // Removed telemetry logging effect - it was causing infinite loops
  // Telemetry data updates frequently from MQTT and doesn't need logging on every update

  // Use ref to track previous position values and only update when they actually change
  const prevPositionRef = useRef({ lat: null, lng: null, heading: null });

  useEffect(() => {
    if (!telemetryValues?.lat || !telemetryValues?.lng) {
      return;
    }

    const lat = telemetryValues.lat;
    const lng = telemetryValues.lng;
    const heading = telemetryValues.heading;

    // Only proceed if position actually changed
    if (
      prevPositionRef.current.lat === lat &&
      prevPositionRef.current.lng === lng &&
      prevPositionRef.current.heading === heading
    ) {
      return; // Position hasn't changed, skip update
    }

    // Update ref
    prevPositionRef.current = { lat, lng, heading };

    // Use telemetryData for full position object, but only when position actually changes
    const newPosition = {
      lat: parseFloat(lat).toFixed(10),
      lng: parseFloat(lng).toFixed(10),
      heading: heading ? parseFloat(heading) : undefined,
      altitude: telemetryData?.aircraftState?.height
        ? parseFloat(telemetryData.aircraftState.height).toFixed(2)
        : undefined,
      speed: telemetryData?.aircraftState?.speed
        ? parseFloat(telemetryData.aircraftState.speed).toFixed(2)
        : undefined,
      flightMode: telemetryData?.aircraftState?.flightMode,
      isFlying: telemetryData?.aircraftState?.isFlying,
      batteryLevel: telemetryData?.powerBattery?.droneBatteryPower,
      gpsSignal: telemetryData?.locationNavigation?.gps?.signalLevel,
      gpsCount: telemetryData?.locationNavigation?.gps?.count,
      distanceToHome: telemetryData?.aircraftState?.distanceToHome
        ? parseFloat(telemetryData.aircraftState.distanceToHome).toFixed(2)
        : undefined,
      attitude: telemetryData?.aircraftState?.attitude,
      windSpeed: telemetryData?.environment?.windSpeed,
      windDirection: telemetryData?.environment?.windDirection,
      deviceStatus: telemetryData?.deviceSystem?.djiDeviceStatus,
      droneName: telemetryData?.deviceSystem?.identification?.droneName,
      timestamp: telemetryData?.deviceSystem?.timestamps?.telemetry,
      flightTimeSec: telemetryData?.aircraftState?.flightTimeSec,
      speedX: telemetryData?.aircraftState?.speedX,
      speedY: telemetryData?.aircraftState?.speedY,
      height: telemetryData?.aircraftState?.height,
    };

    // Only update if the position has actually changed
    setFlightPosition((prevPosition) => {
      if (
        !prevPosition ||
        prevPosition.lat !== newPosition.lat ||
        prevPosition.lng !== newPosition.lng
      ) {
        return newPosition;
      }
      return prevPosition;
    });
  }, [telemetryValues?.lat, telemetryValues?.lng, telemetryValues?.heading]); // Only depend on memoized position values

  // Combined error state for display
  const [combinedError, setCombinedError] = useState(null);

  useEffect(() => {
    if (streamApiError) {
      setCombinedError(streamApiError);
    } else {
      setCombinedError(null);
    }
  }, [streamApiError]);

  // Add a check for expired links before the PIN verification state
  useEffect(() => {
    // Check if the stream link has expired
    if (streamSettings && streamSettings.expiry) {
      const now = Math.floor(Date.now() / 1000);
      if (streamSettings.expiry <= now) {
        console.log("Stream link has expired:", {
          expiry: new Date(streamSettings.expiry * 1000).toISOString(),
          current: new Date(now * 1000).toISOString(),
        });
        setCombinedError("This stream link has expired and cannot be viewed.");
      }
    }
  }, [streamSettings]);

  const handleJoin = (e) => {
    if (e) e.preventDefault();
    console.log(
      "JOIN: Attempting to join stream with settings:",
      streamSettings
    );

    if (streamSettings && streamSettings.token && streamSettings.channel_name) {
      console.log("JOIN: Valid stream settings found, joining stream");
      setIsJoined(true);
      setMobileView("camera"); // Always reset to camera view on join
      setShowMobileMap(false);
      setIsTelemetryExpanded(false);
      setCombinedError(null); // Clear errors on successful join attempt
    } else {
      console.log("JOIN: Invalid stream settings, cannot join", streamSettings);
      setCombinedError(
        "Stream data is not available or incomplete. Cannot join."
      );
      setIsJoined(false);
    }
  };

  const handleLeave = useCallback((errorMessage = null) => {
    console.log(
      "LEAVE: Leaving stream",
      errorMessage ? `with error: ${errorMessage}` : ""
    );
    setIsJoined(false);
    if (errorMessage) {
      setCombinedError(errorMessage);
    }
  }, []);

  // const toggleSettings = () => {
  //   setIsSettingsOpen(!isSettingsOpen);
  // };

  // Update the updateVideoQuality function to consider device type
  const updateVideoQuality = useCallback(
    (quality) => {
      const settings = {
        low: {
          width: isMobile ? 480 : 640,
          height: isMobile ? 270 : 360,
          frameRate: 15,
          bitrateMin: isMobile ? 200 : 400,
          bitrateMax: isMobile ? 500 : 800,
        },
        medium: {
          width: isMobileOrTablet ? 640 : 854,
          height: isMobileOrTablet ? 360 : 480,
          frameRate: isMobileOrTablet ? 15 : 24,
          bitrateMin: isMobileOrTablet ? 400 : 500,
          bitrateMax: isMobileOrTablet ? 800 : 1000,
        },
        high: {
          width: isMobileOrTablet ? 854 : 1280,
          height: isMobileOrTablet ? 480 : 720,
          frameRate: isMobileOrTablet ? 24 : 30,
          bitrateMin: isMobileOrTablet ? 500 : 600,
          bitrateMax: isMobileOrTablet ? 1000 : 1500,
        },
      };

      setVideoSettings((prev) => ({
        ...prev,
        videoConfig: {
          ...prev.videoConfig,
          encoderConfig: settings[quality],
        },
      }));
    },
    [isMobile, isMobileOrTablet]
  );

  const handleTryAgain = () => {
    setCombinedError(null);
    if (!streamSettings && shareCode) {
      retryFetchStreamData(); // Retry fetching stream data if it failed
    } else {
      window.location.reload(); // Fallback to full reload
    }
  };

  // Handle joystick movement
  const handleJoystickChange = (data) => {
    setJoystickData(data);
    // Here you would typically send control commands to the drone
    // based on joystick position
    console.log("Joystick:", data);
  };

  // Debug message for joystick rendering
  useEffect(() => {
    console.log("Joystick control component mounted in StreamingPage");
  }, []);

  // Toggle all widgets visibility
  const toggleAllWidgets = () => {
    setShowAllWidgets(!showAllWidgets);
  };

  // Add/remove body class when widgets visibility changes
  useEffect(() => {
    if (showAllWidgets) {
      document.body.classList.remove("widgets-hidden");
    } else {
      document.body.classList.add("widgets-hidden");
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove("widgets-hidden");
    };
  }, [showAllWidgets]);

  // Add a useEffect to monitor isPinVerified state changes
  useEffect(() => {
    console.log("isPinVerified changed:", isPinVerified);
    console.log("Current state:", {
      isPinVerified,
      isLoadingApi,
      combinedError,
      hasStreamSettings: !!streamSettings,
      hasToken: streamSettings?.token ? true : false,
    });
  }, [isPinVerified, isLoadingApi, combinedError, streamSettings]);

  // Modified PIN verification with CORS considerations
  const handlePinSubmit = async (e) => {
    e.preventDefault();
    console.log("PIN submit called with:", pinInput);
    setIsVerifyingPin(true);
    setPinError("");

    try {
      console.log("Making verification request for code:", shareCode);
      // Add mode: 'cors' explicitly to handle potential CORS issues
      const response = await fetch(
        "https://8b4uaj9s8k.execute-api.eu-west-2.amazonaws.com/Prod/share-code/verify-pin-code",
        {
          method: "POST",
          mode: "cors",
          credentials: "omit",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            pinCode: pinInput,
            code: shareCode,
          }),
        }
      );

      console.log("PIN verification raw response:", response);

      // Handle potential non-JSON responses
      let result;
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") !== -1) {
        result = await response.json();
      } else {
        const text = await response.text();
        console.log("Non-JSON response:", text);
        result = { message: text };
      }

      console.log("PIN verification parsed response:", result);

      // More robust success check
      const isSuccess =
        response.ok &&
        result &&
        result.message &&
        typeof result.message === "string" &&
        result.message.toLowerCase().includes("success");

      console.log("PIN verification success check:", isSuccess);

      if (isSuccess) {
        console.log("PIN verified successfully, setting isPinVerified=true");
        // Force state update with timeout to ensure it propagates
        setIsPinVerified(true);
        console.log("isPinVerified set to true");
      } else {
        console.log("PIN verification failed:", result.message);
        setPinError("Incorrect PIN code. Please try again.");
      }
    } catch (err) {
      console.error("PIN verification error:", err);
      setPinError("Network error. Please try again.");
    } finally {
      setIsVerifyingPin(false);
      console.log("PIN verification process completed, isVerifyingPin=false");
    }
  };

  // Add required state for the feature flags
  const [featureFlags, setFeatureFlags] = useState({
    camerControlEnabled: false,
    telemetryEnabled: false,
    mapEnabled: false,
  });

  // Modify the useEffect for feature flags to also fix potential expiry issues
  useEffect(() => {
    if (streamSettings) {
      console.log("StreamSettings received:", streamSettings);

      // Set feature flags as before
      setFeatureFlags({
        camerControlEnabled: streamSettings.camerControlEnabled || false,
        telemetryEnabled: streamSettings.telemetryEnabled || false,
        mapEnabled: streamSettings.mapEnabled || false,
      });

      // TEMPORARY FIX: Ensure expiry time is reasonable
      const now = Math.floor(Date.now() / 1000);
      if (!streamSettings.expiry || streamSettings.expiry <= now) {
        console.warn(
          "FIXING invalid expiry time. Setting to 5 minutes from now for testing"
        );
        // Create a new object with updated expiry (5 minutes from now)
        const fixedStreamSettings = {
          ...streamSettings,
          expiry: now + 300, // 5 minutes in seconds
        };

        // We can't directly modify the streamSettings from useStreamApi
        // So we'll log this for debugging purposes
        console.log(
          "Using fixed expiry time:",
          new Date(fixedStreamSettings.expiry * 1000).toISOString()
        );

        // This is just for debugging - we can't actually update the streamSettings object directly
        window._fixedStreamSettings = fixedStreamSettings;
      }
    }
  }, [streamSettings]);

  // Add another effect to log the feature flags when they change
  useEffect(() => {
    console.log("Feature flags updated:", featureFlags);
  }, [featureFlags]);

  // Add state for expiry countdown
  const [countdownTime, setCountdownTime] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    expired: false,
  });

  // Add state for 30-second warning
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);
  const [hasShownWarning, setHasShownWarning] = useState(false);

  // Add debug function to examine stream settings
  useEffect(() => {
    if (streamSettings) {
      console.log(
        "DEBUG: Full streamSettings object:",
        JSON.stringify(streamSettings, null, 2)
      );

      // Verify expiry time
      if (streamSettings.expiry) {
        const now = Math.floor(Date.now() / 1000);
        const expiryDate = new Date(streamSettings.expiry * 1000);

        console.log("DEBUG: Current time:", new Date().toISOString());
        console.log("DEBUG: Expiry time:", expiryDate.toISOString());
        console.log(
          "DEBUG: Time until expiry:",
          streamSettings.expiry - now,
          "seconds"
        );

        if (streamSettings.expiry < now) {
          console.warn("DEBUG: WARNING - Expiry time is in the past!");
        }
      } else {
        console.warn("DEBUG: WARNING - No expiry time in stream settings!");
      }
    }
  }, [streamSettings]);

  // Add a memoized fixed version of streamSettings with a valid expiry time
  // Just pass through the stream settings without modification - expiry will be fixed in the backend
  const fixedStreamSettings = useMemo(() => {
    return streamSettings;
  }, [streamSettings]);

  // Update countdown effect to use fixedStreamSettings
  useEffect(() => {
    if (!fixedStreamSettings || !fixedStreamSettings.expiry) {
      console.log(
        "COUNTDOWN: No expiry time found in stream settings, skipping countdown"
      );
      return;
    }

    // Check if expiry time is valid (should always be with our fix)
    const now = Math.floor(Date.now() / 1000);
    if (fixedStreamSettings.expiry <= now) {
      console.warn(
        "COUNTDOWN: Expiry time is in the past, skipping countdown",
        {
          expiry: fixedStreamSettings.expiry,
          current: now,
          difference: fixedStreamSettings.expiry - now,
        }
      );
      return;
    }

    // Only start the timer if the user is joined
    if (!isJoined) {
      console.log("COUNTDOWN: Not joined, skipping countdown");
      return;
    }

    console.log(
      "COUNTDOWN: Starting countdown with expiry:",
      new Date(fixedStreamSettings.expiry * 1000).toISOString()
    );

    const calculateTimeLeft = () => {
      const now = Math.floor(Date.now() / 1000); // current time in seconds
      const timeLeftSeconds = Math.max(0, fixedStreamSettings.expiry - now);

      // console.log('COUNTDOWN: Time left:', timeLeftSeconds, 'seconds');

      if (timeLeftSeconds <= 0) {
        // Time's up!
        console.log("COUNTDOWN: Stream expired, triggering leave");
        setCountdownTime((prev) => ({ ...prev, expired: true }));
        handleLeave("This stream link has expired. Access denied.");
        return null;
      }

      // Calculate days, hours, minutes, seconds
      const days = Math.floor(timeLeftSeconds / (60 * 60 * 24));
      const hours = Math.floor((timeLeftSeconds % (60 * 60 * 24)) / (60 * 60));
      const minutes = Math.floor((timeLeftSeconds % (60 * 60)) / 60);
      const seconds = timeLeftSeconds % 60;

      return { days, hours, minutes, seconds, expired: false };
    };

    // Initial calculation
    const initialTimeLeft = calculateTimeLeft();
    console.log("COUNTDOWN: Initial time calculation:", initialTimeLeft);

    if (initialTimeLeft) {
      setCountdownTime(initialTimeLeft);

      // Set up interval
      const timerId = setInterval(() => {
        const timeLeft = calculateTimeLeft();
        if (timeLeft) {
          setCountdownTime(timeLeft);

          // Show warning at 30 seconds (only once)
          const totalSeconds =
            timeLeft.days * 86400 +
            timeLeft.hours * 3600 +
            timeLeft.minutes * 60 +
            timeLeft.seconds;
          if (totalSeconds <= 30 && !hasShownWarning) {
            setShowExpiryWarning(true);
            setHasShownWarning(true);

            // Auto-dismiss after 10 seconds
            setTimeout(() => {
              setShowExpiryWarning(false);
            }, 10000);
          }
        } else {
          clearInterval(timerId);
        }
      }, 1000);

      // Clean up
      return () => {
        console.log("COUNTDOWN: Cleaning up timer");
        clearInterval(timerId);
      };
    } else {
      console.log(
        "COUNTDOWN: Initial calculation shows expired stream, leaving"
      );
    }
  }, [fixedStreamSettings, isJoined, handleLeave, hasShownWarning]);

  // Update the effect that sets isPinVerified to always require PIN for valid share codes
  useEffect(() => {
    // If we have stream settings, PIN is always required for valid share codes
    if (streamSettings) {
      console.log(
        "Stream settings received, PIN verification will be required"
      );
      setIsPinVerified(false); // Always require PIN verification
    }
  }, [streamSettings]);

  // Add logging before the join button conditional render
  useEffect(() => {
    // Log the conditions for showing join button
    const showJoinButton =
      !isLoadingApi && !combinedError && streamSettings && isPinVerified;
    console.log("Join button visibility conditions:", {
      notLoading: !isLoadingApi,
      noError: !combinedError,
      hasStreamSettings: !!streamSettings,
      isPinVerified,
      shouldShowButton: showJoinButton,
    });
  }, [isLoadingApi, combinedError, streamSettings, isPinVerified]);

  // Add error component with specific styling for expiry errors
  const renderErrorMessage = () => {
    if (!combinedError) return null;

    // Handle different error types
    let errorStr;
    if (typeof combinedError === "string") {
      errorStr = combinedError;
    } else if (combinedError instanceof Error) {
      errorStr = combinedError.message || "An unknown error occurred";
    } else if (typeof combinedError === "object" && combinedError !== null) {
      // Try to get error message without stringifying the whole object
      errorStr =
        combinedError.message ||
        combinedError.error ||
        (Object.prototype.toString.call(combinedError) === "[object Object]"
          ? "An error occurred"
          : combinedError.toString());
    } else {
      errorStr = String(combinedError);
    }

    const isExpiredError = errorStr.toLowerCase().includes("expired");
  };

  // Add debugging for PIN modal visibility - move outside of JSX
  useEffect(() => {
    console.log("PIN modal visibility check:", {
      isPinVerified,
      isLoadingApi,
      hasError: !!combinedError,
      hasStreamSettings: !!streamSettings,
      isExpired: streamSettings?.expiry <= Math.floor(Date.now() / 1000),
      shouldShowModal:
        !isPinVerified &&
        !isLoadingApi &&
        !combinedError &&
        streamSettings &&
        streamSettings?.expiry > Math.floor(Date.now() / 1000),
    });
  }, [isPinVerified, isLoadingApi, combinedError, streamSettings]);

  // Add additional logging to track PIN verification flow
  useEffect(() => {
    if (shareCode) {
      console.log("Share code detected in URL:", shareCode);
      console.log("PIN verification will be required for valid share codes");
    }
  }, [shareCode]);

  // Memoize PIN modal open state to prevent unnecessary re-renders
  const shouldShowPinModal = useMemo(() => {
    return Boolean(
      !isPinVerified &&
        !isLoadingApi &&
        !combinedError &&
        streamSettings &&
        streamSettings.channel_name &&
        fixedStreamSettings &&
        fixedStreamSettings.expiry &&
        fixedStreamSettings.expiry > Math.floor(Date.now() / 1000)
    );
  }, [
    isPinVerified,
    isLoadingApi,
    combinedError,
    streamSettings,
    fixedStreamSettings,
  ]);

  // Add a function to determine the appropriate header text based on the error
  const getHeaderText = () => {
    if (combinedError) {
      // Handle different error types
      let errorStr;
      if (typeof combinedError === "string") {
        errorStr = combinedError;
      } else if (combinedError instanceof Error) {
        errorStr = combinedError.message || "An unknown error occurred";
      } else if (typeof combinedError === "object" && combinedError !== null) {
        // Try to get error message without stringifying the whole object
        errorStr =
          combinedError.message ||
          combinedError.error ||
          (Object.prototype.toString.call(combinedError) === "[object Object]"
            ? "An error occurred"
            : combinedError.toString());
      } else {
        errorStr = String(combinedError);
      }

      if (errorStr.toLowerCase().includes("expired")) {
        return "This link has expired. Access denied";
      }
      return "Inactive stream";
    }
    return "Join Mission Stream";
  };

  // Handle fullscreen changes
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isInFullscreen =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;

      setIsFullscreen(!!isInFullscreen);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange
      );
    };
  }, []);

  // Add the toggle handler:
  const handleViewToggle = (view) => {
    console.log("Toggling view to:", view);

    // Validate if the requested view is enabled
    if (view === "map" && !featureFlags.mapEnabled) {
      console.warn("Map view is disabled, switching to camera");
      view = "camera"; // Fall back to camera
    }

    if (view === "telemetry" && !featureFlags.telemetryEnabled) {
      console.warn("Telemetry view is disabled, switching to camera");
      view = "camera"; // Fall back to camera
    }

    // Multiview requires all three features to be enabled
    if (view === "multiview") {
      const isMultiviewEnabled =
        featureFlags.camerControlEnabled &&
        featureFlags.telemetryEnabled &&
        featureFlags.mapEnabled;
      if (!isMultiviewEnabled) {
        console.warn(
          "Multiview requires all features to be enabled, switching to camera"
        );
        view = "camera"; // Fall back to camera
      }
    }

    // Split screen requires map and camera to be enabled
    if (view === "splitscreen") {
      const isSplitScreenEnabled =
        featureFlags.camerControlEnabled && featureFlags.mapEnabled;
      if (!isSplitScreenEnabled) {
        console.warn(
          "Split screen requires both Map and Camera to be enabled, switching to camera"
        );
        view = "camera"; // Fall back to camera
      }
    }

    // Use startTransition to wrap state updates that trigger lazy component loading
    startTransition(() => {
      setMobileView(view);
      // Sync the showMobileMap state with the view
      setShowMobileMap(view === "map");
      // Telemetry is now always a full-page view, not an overlay
      setIsTelemetryExpanded(false);
    });
  };

  // Swipe gesture handler for mobile view switching
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance (in pixels)
  const minSwipeDistance = 50;

  // Track touch Y position to detect horizontal vs vertical swipes
  const [touchStartY, setTouchStartY] = useState(null);
  const [touchEndY, setTouchEndY] = useState(null);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchEndY(null);
    setTouchStart(e.targetTouches[0].clientX);
    setTouchStartY(e.targetTouches[0].clientY);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
    setTouchEndY(e.targetTouches[0].clientY);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || !touchStartY || !touchEndY) {
      setTouchStart(null);
      setTouchEnd(null);
      setTouchStartY(null);
      setTouchEndY(null);
      return;
    }

    const distanceX = touchStart - touchEnd;
    const distanceY = touchStartY - touchEndY;
    const absDistanceX = Math.abs(distanceX);
    const absDistanceY = Math.abs(distanceY);

    // Only trigger swipe if horizontal movement is greater than vertical (horizontal swipe)
    // and exceeds minimum distance
    if (absDistanceX > minSwipeDistance && absDistanceX > absDistanceY) {
      const isLeftSwipe = distanceX > 0;
      const isRightSwipe = distanceX < 0;

      // Left swipe: move to next view (Map → Camera → Telemetry → Multiview)
      if (isLeftSwipe) {
        if (mobileView === "map") {
          handleViewToggle("camera");
        } else if (mobileView === "camera") {
          handleViewToggle("telemetry");
        } else if (mobileView === "telemetry") {
          handleViewToggle("multiview");
        }
        // Haptic feedback
        if (window.navigator.vibrate) {
          window.navigator.vibrate(10);
        }
      }

      // Right swipe: move to previous view (Multiview → Telemetry → Camera → Map)
      if (isRightSwipe) {
        if (mobileView === "multiview") {
          handleViewToggle("telemetry");
        } else if (mobileView === "telemetry") {
          handleViewToggle("camera");
        } else if (mobileView === "camera") {
          handleViewToggle("map");
        }
        // Haptic feedback
        if (window.navigator.vibrate) {
          window.navigator.vibrate(10);
        }
      }
    }

    // Reset touch state
    setTouchStart(null);
    setTouchEnd(null);
    setTouchStartY(null);
    setTouchEndY(null);
  };

  // isMobile is already available from useResponsive hook

  useEffect(() => {
    console.log("Mobile view state:", {
      isMobile,
      mobileView,
      showMobileMap,
      showAllWidgets,
      mapEnabled: featureFlags.mapEnabled,
    });
  }, [
    isMobile,
    mobileView,
    showMobileMap,
    showAllWidgets,
    featureFlags.mapEnabled,
  ]);

  // Ensure mobile map visibility is synced with view state
  useEffect(() => {
    startTransition(() => {
      if (isMobile && mobileView === "map") {
        setShowMobileMap(true);
      } else {
        setShowMobileMap(false);
      }
    });
  }, [isMobile, mobileView]);

  // ------ Render Logic ------

  // Loading state: primarily for API calls
  if (isLoadingApi && !streamSettings) {
    return (
      <div className="streaming-page">
        <header className="app-header">
          <div className="header-title">
            {/* SVG Logo */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
            >
              <g opacity="0.7">
                <path
                  className="icon-secondary"
                  d="M23 19.2133L12.0193 3L10.6293 8.14496C10.144 10.8733 10.0589 14.9409 11.6789 17.9296C12.3748 19.2119 13.2442 20.2944 14.2867 20.657C14.2867 20.657 15.3006 21.3878 17.6789 20.7159L23 19.2133Z"
                  fill="#95989C"
                ></path>
                <path
                  className="icon-primary"
                  d="M1 19.2133L11.9812 3L13.3716 8.14496C13.857 10.8733 13.9421 14.9409 12.3221 17.9296C11.6261 19.2119 10.7562 20.2944 9.71425 20.657C9.71425 20.657 8.70031 21.3878 6.3221 20.7159L1 19.2133Z"
                  fill="#1B92A6"
                ></path>
              </g>
            </svg>
            <h1>IDI Stream Share</h1>
          </div>
        </header>
        <main className="main-content">
          <div className="join-form-container">
            <div className="join-form">
              <h2>{getHeaderText()}</h2>
              <p className="loading-message">Loading stream details...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Common header for all views
  const AppHeader = (
    <header className={`app-header ${isFullscreen ? "hidden" : ""}`}>
      <div className="header-title">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
        >
          <g opacity="0.7">
            <path
              className="icon-secondary"
              d="M23 19.2133L12.0193 3L10.6293 8.14496C10.144 10.8733 10.0589 14.9409 11.6789 17.9296C12.3748 19.2119 13.2442 20.2944 14.2867 20.657C14.2867 20.657 15.3006 21.3878 17.6789 20.7159L23 19.2133Z"
              fill="#95989C"
            ></path>
            <path
              className="icon-primary"
              d="M1 19.2133L11.9812 3L13.3716 8.14496C13.857 10.8733 13.9421 14.9409 12.3221 17.9296C11.6261 19.2119 10.7562 20.2944 9.71425 20.657C9.71425 20.657 8.70031 21.3878 6.3221 20.7159L1 19.2133Z"
              fill="#1B92A6"
            ></path>
          </g>
        </svg>
        <h1>IDI Stream Share</h1>
      </div>

      <div className="header-actions">
        {countdownTime &&
          !countdownTime.expired &&
          (() => {
            const totalSeconds =
              countdownTime.days * 86400 +
              countdownTime.hours * 3600 +
              countdownTime.minutes * 60 +
              countdownTime.seconds;
            let timerClass = "countdown-timer";

            if (totalSeconds <= 30) {
              timerClass += " critical"; // Red
            } else if (totalSeconds <= 60) {
              timerClass += " warning"; // Amber
            } else {
              timerClass += " normal"; // Green
            }

            return (
              <div className={timerClass}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                <span>
                  {countdownTime.days > 0 && `${countdownTime.days}d `}
                  {countdownTime.hours.toString().padStart(2, "0")}:
                  {countdownTime.minutes.toString().padStart(2, "0")}:
                  {countdownTime.seconds.toString().padStart(2, "0")}
                </span>
              </div>
            );
          })()}

        <button className="leave-button" onClick={handleLeave}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
            <polyline points="16 17 21 12 16 7"></polyline>
            <line x1="21" y1="12" x2="9" y2="12"></line>
          </svg>
          <span>Leave Stream</span>
        </button>
      </div>
    </header>
  );

  // Not joined or error state
  if (
    !isJoined ||
    combinedError ||
    (isJoined &&
      (!streamSettings ||
        !streamSettings.token ||
        !streamSettings.channel_name))
  ) {
    return (
      <div className={`streaming-page ${isFullscreen ? "fullscreen" : ""}`}>
        {AppHeader}
        <main className="main-content">
          <div className="join-form-container">
            <div className="join-form">
              <h2>{getHeaderText()}</h2>
              {isLoadingApi && (
                <p className="loading-message">Loading stream details...</p>
              )}
              {combinedError && renderErrorMessage()}

              {!isLoadingApi &&
                !combinedError &&
                !streamSettings &&
                shareCode && (
                  <p className="loading-message">
                    Fetching stream data for code: {shareCode}...
                  </p>
                )}

              {/* PIN code entry step */}
              <ReactModal
                isOpen={shouldShowPinModal}
                contentLabel="PIN Verification"
                className="pin-modal"
                overlayClassName="pin-modal-overlay"
                shouldCloseOnOverlayClick={false}
                shouldCloseOnEsc={false}
                ariaHideApp={false}
                portalClassName="pin-modal-portal"
                parentSelector={() => document.body}
              >
                <form onSubmit={handlePinSubmit}>
                  <h2 style={{ marginTop: 0 }}>Enter PIN Code</h2>
                  <div className="form-group">
                    <label htmlFor="pin-input">
                      This stream is protected.
                      <br />
                      <br />
                      Enter the 6-digit PIN:
                    </label>
                    <input
                      id="pin-input"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      minLength={6}
                      value={pinInput}
                      onChange={(e) => {
                        setPinInput(e.target.value.replace(/[^0-9]/g, ""));
                        setPinError("");
                      }}
                      autoFocus
                      disabled={isVerifyingPin}
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      data-1p-ignore
                      data-lpignore="true"
                      data-form-type="other"
                      data-bwignore
                      style={{ WebkitTextSecurity: "disc" }}
                    />
                    {pinError && (
                      <p className="pin-error-message">{pinError}</p>
                    )}
                  </div>
                  <button
                    type="submit"
                    className="join-button"
                    disabled={pinInput.length !== 6 || isVerifyingPin}
                  >
                    {isVerifyingPin ? "Verifying..." : "Submit PIN"}
                  </button>
                </form>
              </ReactModal>

              {/* Join button shown only after PIN verification succeeds */}
              {!isLoadingApi &&
                !combinedError &&
                streamSettings &&
                isPinVerified && (
                  <>
                    {fixedStreamSettings &&
                    fixedStreamSettings.expiry &&
                    fixedStreamSettings.expiry >
                      Math.floor(Date.now() / 1000) ? (
                      <form onSubmit={handleJoin}>
                        <button
                          type="submit"
                          className="join-button"
                          disabled={!streamSettings || !streamSettings.token}
                        >
                          {/* Connect Icon SVG */}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="12" r="10"></circle>
                            <polygon points="10 8 16 12 10 16 10 8"></polygon>
                          </svg>
                          Connect to Stream
                        </button>
                      </form>
                    ) : (
                      <div
                        className="error-message"
                        style={{ marginTop: "1rem", textAlign: "center" }}
                      >
                        This stream link has expired and cannot be viewed.
                      </div>
                    )}
                  </>
                )}
              {!shareCode && !isLoadingApi && !combinedError && (
                <p className="info-message">
                  Share code not found in URL. Please use a valid share link.
                </p>
              )}
              {(combinedError ||
                (isJoined && (!streamSettings || !streamSettings.token))) && (
                <button
                  onClick={handleTryAgain}
                  className="join-button"
                  style={{ marginTop: "1rem" }}
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Expiry warning popup
  const ExpiryWarningPopup = showExpiryWarning && (
    <div className="expiry-warning-overlay">
      <div className="expiry-warning-popup">
        <div className="warning-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        <h3>Stream Ending Soon</h3>
        <p>This stream will end in less than 30 seconds.</p>
        <button
          className="warning-close-button"
          onClick={() => setShowExpiryWarning(false)}
        >
          Close
        </button>
      </div>
    </div>
  );

  // Joined and ready to stream
  const mainContent = (
    <>
      {/* Show view toggle on mobile/tablet AND desktop */}
      <MobileViewToggle
        currentView={mobileView}
        onToggle={handleViewToggle}
        featureFlags={featureFlags}
      />
      <main
        className="main-content"
        onTouchStart={isMobileOrTablet ? onTouchStart : undefined}
        onTouchMove={isMobileOrTablet ? onTouchMove : undefined}
        onTouchEnd={isMobileOrTablet ? onTouchEnd : undefined}
      >
        <div
          className="streaming-container"
          style={{
            display: mobileView === "camera" ? "flex" : "none",
          }}
        >
          <AgoraStreamingClient
            appId={appId}
            channel={streamSettings.channel_name}
            token={streamSettings.token}
            role={role}
            videoSettings={videoSettings}
            isFullscreen={isFullscreen}
          />

          <div className={`overlay-widgets ${isFullscreen ? "hidden" : ""}`}>
            {/* Temporarily disabled - Camera controls modal */}
            {/* <div className="controls-container" style={{display: (showAllWidgets && featureFlags.camerControlEnabled) ? 'block' : 'none'}}>
              <CamWidget />
            </div> */}

            {/* Add Joystick Control - hide on desktop and in multiview */}
            {!isDesktop &&
              (!isMobileOrTablet ||
                (isMobileOrTablet && mobileView !== "multiview")) && (
                <JoystickControl onChange={handleJoystickChange} size={600} />
              )}
          </div>

          {/* Clear view toggle button - positioned in bottom left (eye icon) */}
          <button
            className={`widgets-toggle-icon clear-view-toggle bottom-left ${
              isFullscreen ? "hidden" : ""
            }`}
            onClick={toggleAllWidgets}
            title={
              showAllWidgets ? "Clear View (Hide Widgets)" : "Show Widgets"
            }
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {showAllWidgets ? (
                <>
                  {/* Eye icon */}
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </>
              ) : (
                <>
                  {/* Eye-off icon */}
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </>
              )}
            </svg>
          </button>

          {isSettingsOpen && (
            <div className="settings-panel">
              <div className="settings-header">
                <h3>Stream Settings</h3>
                <button className="close-button" onClick={toggleSettings}>
                  ×
                </button>
              </div>
              <div className="settings-content">
                <div className="settings-group">
                  <h4>Video Quality</h4>
                  <div className="radio-group">
                    {["low", "medium", "high"].map((q) => (
                      <label key={q}>
                        <input
                          type="radio"
                          name="quality"
                          value={q}
                          checked={
                            videoSettings.videoConfig.encoderConfig.height ===
                            (q === "low" ? 180 : q === "medium" ? 360 : 720)
                          }
                          onChange={() => updateVideoQuality(q)}
                        />
                        {q.charAt(0).toUpperCase() + q.slice(1)} (
                        {q === "low" ? 180 : q === "medium" ? 360 : 720}p)
                      </label>
                    ))}
                  </div>
                </div>
                <div className="settings-group">
                  <h4>Audio Settings</h4>
                  <div className="checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={videoSettings.audioConfig.AEC}
                        onChange={() =>
                          setVideoSettings((prev) => ({
                            ...prev,
                            audioConfig: {
                              ...prev.audioConfig,
                              AEC: !prev.audioConfig.AEC,
                            },
                          }))
                        }
                      />
                      Echo Cancellation
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={videoSettings.audioConfig.AGC}
                        onChange={() =>
                          setVideoSettings((prev) => ({
                            ...prev,
                            audioConfig: {
                              ...prev.audioConfig,
                              AGC: !prev.audioConfig.AGC,
                            },
                          }))
                        }
                      />
                      Auto Gain Control
                    </label>
                  </div>
                </div>
                <div className="settings-info">
                  <p>
                    Video quality changes apply upon rejoining or next stream.
                    Audio settings apply immediately if possible, or on rejoin.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Telemetry widgets moved outside streaming container */}
        <div
          className="telemetry-widgets-container"
          style={{
            display:
              showAllWidgets && featureFlags.telemetryEnabled ? "flex" : "none",
          }}
        >
          <UAVRCTelemetry
            uavData={{
              isConnected: isMqttConnected,
              model:
                telemetryData?.deviceSystem?.identification?.droneName ||
                "DJI Mavic 3T",
              batteryLevel: telemetryData?.powerBattery?.droneBatteryPower || 0,
              flightTimeLeft:
                telemetryData?.powerBattery?.flightTimeRemaining || 160,
              satLocks: telemetryData?.locationNavigation?.gps?.count || 0,
              airTraffic: "None",
              // New data fields for enhanced display
              flightMode: telemetryData?.aircraftState?.flightMode,
              isFlying: telemetryData?.aircraftState?.isFlying || false,
              altitude: telemetryData?.aircraftState?.height
                ? parseFloat(telemetryData.aircraftState.height).toFixed(2)
                : "0.00",
              speed: telemetryData?.aircraftState?.speed
                ? parseFloat(telemetryData.aircraftState.speed).toFixed(2)
                : "0.00",
              windSpeed: telemetryData?.environment?.windSpeed
                ? parseFloat(telemetryData.environment.windSpeed).toFixed(2)
                : "0.00",
              windDirection: telemetryData?.environment?.windDirection || 0,
              deviceStatus: telemetryData?.deviceSystem?.djiDeviceStatus,
            }}
            rcData={{
              isConnected: true,
              model:
                telemetryData?.deviceSystem?.identification?.rcName ||
                "DJI Smart Controller Enterprise",
              batteryLevel: telemetryData?.powerBattery?.rcBatteryPower || 0,
              signalStrength: telemetryData?.controlCommands?.rcSignal || 0,
            }}
          />

          <CoordinatesTelemetry
            data={{
              heading: telemetryData?.aircraftState
                ? parseFloat(telemetryData.aircraftState.heading).toFixed(2)
                : "",
              longitude: telemetryData?.locationNavigation
                ? parseFloat(
                    telemetryData.locationNavigation.longitude
                  ).toFixed(10)
                : "",
              latitude: telemetryData?.locationNavigation
                ? parseFloat(telemetryData.locationNavigation.latitude).toFixed(
                    10
                  )
                : "",
              verticalSpeed: telemetryData?.aircraftState
                ? parseFloat(telemetryData.aircraftState.speedY).toFixed(2)
                : "",
              horizontalSpeed: telemetryData?.aircraftState
                ? parseFloat(telemetryData.aircraftState.speedX).toFixed(2)
                : "",
              flightTime: telemetryData?.aircraftState
                ? parseInt(telemetryData.aircraftState.flightTimeSec)
                : "",
              distanceFromHome: telemetryData?.aircraftState
                ? parseFloat(
                    telemetryData.aircraftState.distanceToHome
                  ).toFixed(2)
                : "",
              // Additional navigation data
              altitude: telemetryData?.aircraftState
                ? parseFloat(telemetryData.aircraftState.height).toFixed(2)
                : "",
              attitude: telemetryData?.aircraftState?.attitude || {
                pitch: 0,
                roll: 0,
                yaw: 0,
              },
              gpsSignal: telemetryData?.locationNavigation?.gps?.count || 0,
              gpsCount: telemetryData?.locationNavigation?.gps?.count || 0,
              isFlying: telemetryData?.aircraftState?.isFlying || false,
            }}
          />
        </div>

        {/* Add Map/Camera PiP component outside streaming container - Desktop only */}
        {/* When in Camera view: show Map. When in Map view: show Camera */}
        {!isMobileOrTablet && featureFlags.telemetryEnabled && (
          <div
            className={`drone-map-container ${isFullscreen ? "hidden" : ""}`}
            style={{ display: showAllWidgets ? "block" : "none" }}
          >
            {mobileView === "camera" && featureFlags.mapEnabled ? (
              // Show Map when viewing Camera
              <DroneMap
                position={flightPosition}
                isConnected={isMqttConnected}
                onSwitchToSplitScreen={() => handleViewToggle("splitscreen")}
              />
            ) : mobileView === "map" && featureFlags.camerControlEnabled ? (
              // Show Camera when viewing Map
              <CameraPiPView isConnected={isJoined}>
                <AgoraStreamingClient
                  appId={appId}
                  channel={streamSettings.channel_name}
                  token={streamSettings.token}
                  role={role}
                  videoSettings={videoSettings}
                  isFullscreen={false}
                />
              </CameraPiPView>
            ) : null}
          </div>
        )}

        {/* Map View - Mobile, Tablet, and Desktop */}
        {mobileView === "map" && (
          <div
            className="mobile-map-view visible"
            onTouchStart={
              isMobileOrTablet && isLandscape ? undefined : onTouchStart
            }
            onTouchMove={
              isMobileOrTablet && isLandscape ? undefined : onTouchMove
            }
            onTouchEnd={
              isMobileOrTablet && isLandscape ? undefined : onTouchEnd
            }
          >
            <Suspense
              fallback={<LogoLoader size={40} message="Loading map..." />}
            >
              <MobileMap
                position={flightPosition}
                isConnected={isMqttConnected}
                onSwitchToCamera={() => handleViewToggle("camera")}
              />
            </Suspense>
          </div>
        )}

        {/* Telemetry Full-Screen View - Mobile, Tablet, and Desktop */}
        {mobileView === "telemetry" && (
          <div
            className="mobile-telemetry-view visible"
            // Swipe handlers only on mobile/tablet portrait
            onTouchStart={
              isMobileOrTablet && isPortrait ? onTouchStart : undefined
            }
            onTouchMove={
              isMobileOrTablet && isPortrait ? onTouchMove : undefined
            }
            onTouchEnd={isMobileOrTablet && isPortrait ? onTouchEnd : undefined}
          >
            <div className="telemetry-fullscreen-content">
              {/* No close button - use toggle buttons to switch views */}
              <MobileStatusWidget
                batteryLevel={telemetryValues?.batteryLevel ?? 0}
                rcBatteryLevel={telemetryValues?.rcBatteryLevel ?? 0}
                gpsCount={telemetryValues?.gpsCount ?? 0}
                altitude={telemetryValues?.altitude ?? 0}
                heading={telemetryValues?.heading ?? 0}
                verticalSpeed={telemetryValues?.verticalSpeed ?? 0}
                horizontalSpeed={telemetryValues?.horizontalSpeed ?? 0}
                flightMode={telemetryValues?.flightMode ?? "MANUAL"}
                distanceFromHome={telemetryValues?.distanceFromHome ?? 0}
                isFlying={telemetryValues?.isFlying ?? false}
                isConnected={isMqttConnected}
                latitude={telemetryValues?.lat}
                longitude={telemetryValues?.lng}
                isFullScreen={true}
              />
            </div>
          </div>
        )}

        {/* Multiview - Map, Video, and Telemetry in one view - All Devices */}
        {mobileView === "multiview" && (
          <MultiviewLayout
            videoComponent={
              <div className="multiview-video-wrapper">
                {isJoined ? (
                  <AgoraStreamingClient
                    appId={appId}
                    channel={streamSettings.channel_name}
                    token={streamSettings.token}
                    role={role}
                    videoSettings={videoSettings}
                    isFullscreen={false}
                  />
                ) : (
                  <div className="multiview-video-placeholder">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                      <circle cx="12" cy="13" r="4"></circle>
                    </svg>
                    <p>Join stream to view camera feed</p>
                  </div>
                )}
              </div>
            }
            mapComponent={
              <Suspense
                fallback={<LogoLoader size={40} message="Loading map..." />}
              >
                <MobileMap
                  position={flightPosition}
                  isConnected={isMqttConnected}
                  onSwitchToCamera={() => handleViewToggle("camera")}
                  mapEnabled={featureFlags.mapEnabled}
                />
              </Suspense>
            }
            telemetryComponent={
              <MobileStatusWidget
                batteryLevel={telemetryValues?.batteryLevel ?? 0}
                rcBatteryLevel={telemetryValues?.rcBatteryLevel ?? 0}
                gpsCount={telemetryValues?.gpsCount ?? 0}
                altitude={telemetryValues?.altitude ?? 0}
                heading={telemetryValues?.heading ?? 0}
                verticalSpeed={telemetryValues?.verticalSpeed ?? 0}
                horizontalSpeed={telemetryValues?.horizontalSpeed ?? 0}
                flightMode={telemetryValues?.flightMode ?? "MANUAL"}
                distanceFromHome={telemetryValues?.distanceFromHome ?? 0}
                isFlying={telemetryValues?.isFlying ?? false}
                isConnected={isMqttConnected}
                latitude={telemetryValues?.lat}
                longitude={telemetryValues?.lng}
                isFullScreen={true}
                isCompact={true}
              />
            }
          />
        )}

        {/* Split Screen - Map Left, Camera Right - Desktop Only */}
        {mobileView === "splitscreen" && (
          <div className="split-screen-view">
            <SplitScreenLayout
              leftComponent={
                <div className="split-screen-left-panel">
                  <Suspense
                    fallback={<LogoLoader size={40} message="Loading map..." />}
                  >
                    <MobileMap
                      position={flightPosition}
                      isConnected={isMqttConnected}
                      onSwitchToCamera={() => handleViewToggle("camera")}
                    />
                  </Suspense>

                  {/* Telemetry widgets on left side with map */}
                  {featureFlags.telemetryEnabled && (
                    <div className="split-screen-telemetry">
                      <UAVRCTelemetry
                        uavData={{
                          isConnected: isMqttConnected,
                          model:
                            telemetryData?.deviceSystem?.identification
                              ?.droneName || "DJI Mavic 3T",
                          batteryLevel:
                            telemetryData?.powerBattery?.droneBatteryPower || 0,
                          flightTimeLeft:
                            telemetryData?.powerBattery?.flightTimeRemaining ||
                            160,
                          satLocks:
                            telemetryData?.locationNavigation?.gps?.count || 0,
                          airTraffic: "None",
                          flightMode: telemetryData?.aircraftState?.flightMode,
                          isFlying:
                            telemetryData?.aircraftState?.isFlying || false,
                          altitude: telemetryData?.aircraftState?.height
                            ? parseFloat(
                                telemetryData.aircraftState.height
                              ).toFixed(2)
                            : "0.00",
                          speed: telemetryData?.aircraftState?.speed
                            ? parseFloat(
                                telemetryData.aircraftState.speed
                              ).toFixed(2)
                            : "0.00",
                          windSpeed: telemetryData?.environment?.windSpeed
                            ? parseFloat(
                                telemetryData.environment.windSpeed
                              ).toFixed(2)
                            : "0.00",
                          windDirection:
                            telemetryData?.environment?.windDirection || 0,
                          deviceStatus:
                            telemetryData?.deviceSystem?.djiDeviceStatus,
                        }}
                        rcData={{
                          isConnected: true,
                          model:
                            telemetryData?.deviceSystem?.identification
                              ?.rcName || "DJI Smart Controller Enterprise",
                          batteryLevel:
                            telemetryData?.powerBattery?.rcBatteryPower || 0,
                          signalStrength:
                            telemetryData?.controlCommands?.rcSignal || 0,
                        }}
                      />

                      <CoordinatesTelemetry
                        data={{
                          heading: telemetryData?.aircraftState?.heading
                            ? parseFloat(
                                telemetryData.aircraftState.heading
                              ).toFixed(2)
                            : "",
                          longitude: telemetryData?.locationNavigation
                            ?.longitude
                            ? parseFloat(
                                telemetryData.locationNavigation.longitude
                              ).toFixed(10)
                            : "",
                          latitude: telemetryData?.locationNavigation?.latitude
                            ? parseFloat(
                                telemetryData.locationNavigation.latitude
                              ).toFixed(10)
                            : "",
                          verticalSpeed: telemetryData?.aircraftState?.speedY
                            ? parseFloat(
                                telemetryData.aircraftState.speedY
                              ).toFixed(2)
                            : "",
                          horizontalSpeed: telemetryData?.aircraftState?.speedX
                            ? parseFloat(
                                telemetryData.aircraftState.speedX
                              ).toFixed(2)
                            : "",
                          flightTime: telemetryData?.aircraftState
                            ?.flightTimeSec
                            ? parseInt(
                                telemetryData.aircraftState.flightTimeSec
                              )
                            : "",
                          distanceFromHome: telemetryData?.aircraftState
                            ?.distanceToHome
                            ? parseFloat(
                                telemetryData.aircraftState.distanceToHome
                              ).toFixed(2)
                            : "",
                          altitude: telemetryData?.aircraftState?.height
                            ? parseFloat(
                                telemetryData.aircraftState.height
                              ).toFixed(2)
                            : "",
                          attitude: telemetryData?.aircraftState?.attitude || {
                            pitch: 0,
                            roll: 0,
                            yaw: 0,
                          },
                          gpsSignal:
                            telemetryData?.locationNavigation?.gps?.count || 0,
                          gpsCount:
                            telemetryData?.locationNavigation?.gps?.count || 0,
                          isFlying:
                            telemetryData?.aircraftState?.isFlying || false,
                        }}
                      />
                    </div>
                  )}
                </div>
              }
              rightComponent={
                <div className="split-screen-camera-wrapper">
                  {isJoined ? (
                    <AgoraStreamingClient
                      appId={appId}
                      channel={streamSettings.channel_name}
                      token={streamSettings.token}
                      role={role}
                      videoSettings={videoSettings}
                      isFullscreen={false}
                    />
                  ) : (
                    <div className="split-screen-camera-placeholder">
                      <LogoLoader size={60} message="Connecting to stream..." />
                    </div>
                  )}

                  {/* Camera controls on right side with camera */}
                  {/* Temporarily disabled - Camera controls modal */}
                  {/* {featureFlags.camerControlEnabled && (
                    <div className="split-screen-camera-controls">
                      <CamWidget />
                    </div>
                  )} */}
                </div>
              }
            />
          </div>
        )}
      </main>
    </>
  );

  // Mobile Status Widget footer component - hide when telemetry or multiview is active, only show on mobile/tablet camera/map view
  const mobileStatusFooter =
    isMobileOrTablet &&
    !isTelemetryExpanded &&
    mobileView !== "telemetry" &&
    mobileView !== "multiview" &&
    mobileView !== "map" ? (
      <div className="mobile-only">
        <MobileStatusWidget
          batteryLevel={telemetryValues?.batteryLevel ?? 0}
          rcBatteryLevel={telemetryValues?.rcBatteryLevel ?? 0}
          gpsCount={telemetryValues?.gpsCount ?? 0}
          altitude={telemetryValues?.altitude ?? 0}
          heading={telemetryValues?.heading ?? 0}
          verticalSpeed={telemetryValues?.verticalSpeed ?? 0}
          horizontalSpeed={telemetryValues?.horizontalSpeed ?? 0}
          flightMode={telemetryValues?.flightMode ?? "MANUAL"}
          distanceFromHome={telemetryValues?.distanceFromHome ?? 0}
          isFlying={telemetryValues?.isFlying ?? false}
          isConnected={isMqttConnected}
          latitude={telemetryValues?.lat}
          longitude={telemetryValues?.lng}
        />
      </div>
    ) : null;

  // Use PWA Layout for mobile/tablet in PWA mode, otherwise use regular layout
  if (isPWAMode && isMobileOrTablet) {
    return (
      <>
        <PWALayout
          header={AppHeader}
          footer={mobileStatusFooter}
          className={`pwa-mode ${isFullscreen ? "fullscreen" : ""} ${
            isTelemetryExpanded ? "telemetry-open" : ""
          }`}
        >
          {mainContent}
        </PWALayout>
        {ExpiryWarningPopup}
        <PWAInstallPrompt />
      </>
    );
  }

  // Regular layout for browser mode or desktop
  return (
    <>
      <div
        className={`streaming-page ${isFullscreen ? "fullscreen" : ""} ${
          isPWAMode ? "pwa-mode" : ""
        }`}
      >
        {AppHeader}
        {mainContent}
        {mobileStatusFooter}
      </div>
      {ExpiryWarningPopup}
      {!isPWAMode && <PWAInstallPrompt />}
    </>
  );
};

// Set app element for ReactModal only once
if (
  typeof window !== "undefined" &&
  !document.getElementById("root").hasAttribute("data-modal-app-set")
) {
  ReactModal.setAppElement("#root");
  document.getElementById("root").setAttribute("data-modal-app-set", "true");
}

export default StreamingPage;
